import envConfig from "../../../config/envConfig";
import JSZip from "jszip";

/**
 * In-memory cache for presigned download URLs to prevent redundant backend invocations.
 * Key: filePath -> Value: { url: string, expiresAt: number }
 */
const downloadUrlCache = new Map();

/**
 * Convert base64 data URL to standard Blob.
 * @param {string} dataUrl
 * @returns {Blob|null}
 */
export const dataUrlToBlob = (dataUrl) => {
  if (!dataUrl || typeof dataUrl !== "string") return null;
  if (!dataUrl.startsWith("data:")) return null;

  try {
    const commaIdx = dataUrl.indexOf(",");
    if (commaIdx === -1) return null;
    const header = dataUrl.substring(0, commaIdx);
    const dataPart = dataUrl.substring(commaIdx + 1).trim();
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const bstr = atob(dataPart);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  } catch (err) {
    console.warn("Failed to convert dataUrl to Blob:", err);
    return null;
  }
};

/**
 * Convert Blob or File to base64 Data URL.
 * @param {Blob|File} blob
 * @returns {Promise<string>}
 */
export const blobToDataUrl = (blob) => {
  return new Promise((resolve, reject) => {
    if (!blob) return resolve("");
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = (err) => reject(err);
    reader.readAsDataURL(blob);
  });
};

/**
 * Request an AWS S3 presigned URL from the backend Firebase Cloud Function.
 * @param {Object} options
 * @param {string} options.filePath - Relative S3 key (e.g., "projects/123/abc.sb3")
 * @param {"put"|"get"} [options.operation="put"] - Upload (put) or Download (get)
 * @param {string} [options.contentType] - MIME type for PUT uploads
 * @param {number} [options.expiresInSeconds=900] - Expiration duration in seconds
 * @returns {Promise<{ presignedUrl: string, filePath: string, operation: string, contentType: string, expiresIn: number }>}
 */
export const getS3PresignedUrl = async ({
  filePath,
  operation = "put",
  contentType,
  expiresInSeconds = 900,
}) => {
  if (!filePath || typeof filePath !== "string") {
    throw new Error("filePath string is required for S3 presigned URL request");
  }

  const endpoint = `${envConfig.backendUrl}/getS3SignedUrlV1`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000);

  let response;
  try {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filePath: filePath.replace(/^\/+/, ""),
        operation,
        contentType,
        expiresInSeconds,
      }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
  } catch (fetchErr) {
    clearTimeout(timeoutId);
    if (fetchErr.name === "AbortError") {
      throw new Error(
        `Storage signer request timed out (15s). Ensure Firebase functions emulator is running on ${envConfig.backendUrl}`
      );
    }
    throw fetchErr;
  }

  if (!response.ok) {
    let errMsg = `Failed to get presigned URL (${response.status})`;
    try {
      const errData = await response.json();
      if (errData && errData.message) errMsg = errData.message;
    } catch (e) {}
    throw new Error(errMsg);
  }

  const data = await response.json();
  if (!data.success || !data.presignedUrl) {
    throw new Error(data.message || "Failed to acquire S3 signed URL.");
  }

  return data;
};

/**
 * Upload a binary Blob, ArrayBuffer, or string directly to AWS S3 using presigned PUT.
 * @param {string} filePath - S3 Key (e.g., "projects/student_1/proj_123.zip")
 * @param {Blob|ArrayBuffer|string} filePayload - Content to upload
 * @param {string} [contentType] - Optional MIME type override
 * @returns {Promise<{ success: boolean, filePath: string }>}
 */
export const uploadToS3 = async (filePath, filePayload, contentType) => {
  let bodyBlob;
  let resolvedContentType = contentType;

  if (typeof filePayload === "string" && filePayload.startsWith("data:")) {
    bodyBlob = dataUrlToBlob(filePayload);
    if (!resolvedContentType && bodyBlob) {
      resolvedContentType = bodyBlob.type;
    }
  } else if (filePayload instanceof Blob) {
    bodyBlob = filePayload;
    if (!resolvedContentType) {
      resolvedContentType = bodyBlob.type || "application/octet-stream";
    }
  } else if (filePayload instanceof ArrayBuffer) {
    bodyBlob = new Blob([filePayload], {
      type: resolvedContentType || "application/octet-stream",
    });
  } else if (filePayload instanceof Uint8Array) {
    bodyBlob = new Blob([filePayload.buffer], {
      type: resolvedContentType || "application/octet-stream",
    });
  } else if (typeof filePayload === "string") {
    // Check if string is raw Base64 (e.g. from exportSprite or binary files without data: prefix)
    const isBinaryMime =
      resolvedContentType &&
      (resolvedContentType.startsWith("image/") ||
        resolvedContentType.includes("scratch") ||
        resolvedContentType.includes("zip") ||
        resolvedContentType.includes("octet-stream"));
    const looksLikeBase64 =
      filePayload.startsWith("UEsDB") ||
      (filePayload.length > 50 &&
        !filePayload.includes("\n") &&
        /^[A-Za-z0-9+/=]+$/.test(filePayload.substring(0, 100)));

    if (looksLikeBase64 || isBinaryMime) {
      try {
        const bstr = atob(filePayload.trim());
        let n = bstr.length;
        const u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        bodyBlob = new Blob([u8arr], {
          type: resolvedContentType || "application/octet-stream",
        });
      } catch (e) {
        bodyBlob = new Blob([filePayload], {
          type: resolvedContentType || "text/plain",
        });
      }
    } else {
      bodyBlob = new Blob([filePayload], {
        type: resolvedContentType || "text/plain",
      });
    }
  } else if (typeof filePayload === "object") {
    bodyBlob = new Blob([JSON.stringify(filePayload, null, 2)], {
      type: resolvedContentType || "application/json",
    });
  } else {
    throw new Error("Invalid payload format for S3 upload.");
  }

  const { presignedUrl, contentType: backendContentType } =
    await getS3PresignedUrl({
      filePath,
      operation: "put",
      contentType: resolvedContentType,
    });

  const uploadHeaders = {
    "Content-Type": resolvedContentType || backendContentType || "application/octet-stream",
  };

  const uploadResponse = await fetch(presignedUrl, {
    method: "PUT",
    headers: uploadHeaders,
    body: bodyBlob,
  });

  if (!uploadResponse.ok) {
    let detail = "";
    try {
      const errXml = await uploadResponse.text();
      const codeMatch = errXml.match(/<Code>(.*?)<\/Code>/);
      const msgMatch = errXml.match(/<Message>(.*?)<\/Message>/);
      if (codeMatch) detail = ` (${codeMatch[1]}${msgMatch ? `: ${msgMatch[1]}` : ""})`;
    } catch (e) {}

    throw new Error(
      `Direct upload to S3 failed [${uploadResponse.status} ${uploadResponse.statusText}]${detail}. Ensure IAM policy permits s3:PutObject and bucket CORS allows PUT.`
    );
  }

  // Invalidate any stale GET cache for this key
  downloadUrlCache.delete(filePath);

  return { success: true, filePath };
};

/**
 * Get a cached or fresh presigned GET download URL for an S3 key.
 * @param {string} filePath
 * @param {number} [expiresInSeconds=1800]
 * @returns {Promise<string>}
 */
export const getS3DownloadUrl = async (filePath, expiresInSeconds = 1800) => {
  if (!filePath) return "";
  // If already a full URL or data URI, return directly
  if (
    filePath.startsWith("http://") ||
    filePath.startsWith("https://") ||
    filePath.startsWith("data:")
  ) {
    return filePath;
  }

  const cached = downloadUrlCache.get(filePath);
  const now = Date.now();
  // Return cached URL if it has at least 60 seconds of validity left
  if (cached && cached.expiresAt > now + 60000) {
    return cached.url;
  }

  const { presignedUrl, expiresIn } = await getS3PresignedUrl({
    filePath,
    operation: "get",
    expiresInSeconds,
  });

  const expiresAt = now + (expiresIn || expiresInSeconds) * 1000;
  downloadUrlCache.set(filePath, { url: presignedUrl, expiresAt });

  return presignedUrl;
};

/**
 * Download a file from AWS S3 via presigned GET.
 * @param {string} filePath - S3 Key
 * @param {"blob"|"json"|"text"|"arrayBuffer"} [responseType="blob"]
 * @returns {Promise<any>}
 */
export const downloadFromS3 = async (filePath, responseType = "blob") => {
  const downloadUrl = await getS3DownloadUrl(filePath);

  const response = await fetch(downloadUrl);
  if (!response.ok) {
    let detail = "";
    try {
      const errXml = await response.text();
      const codeMatch = errXml.match(/<Code>(.*?)<\/Code>/);
      const msgMatch = errXml.match(/<Message>(.*?)<\/Message>/);
      if (codeMatch) detail = ` (${codeMatch[1]}${msgMatch ? `: ${msgMatch[1]}` : ""})`;
    } catch (e) {}

    throw new Error(
      `Failed to download asset from S3 [${response.status} ${response.statusText}]${detail}. Ensure IAM policy permits s3:GetObject and bucket CORS allows GET.`
    );
  }

  switch (responseType) {
    case "json":
      return await response.json();
    case "text":
      return await response.text();
    case "arrayBuffer":
      return await response.arrayBuffer();
    case "blob":
    default:
      return await response.blob();
  }
};

/**
 * Package Python or JavaScript project files/code into a single .zip Blob.
 * @param {Object} params
 * @param {string} [params.code] - Single entry code
 * @param {Array} [params.files] - Multi-file array
 * @param {string} params.environment - "python" | "javascript"
 * @param {string} [params.entryPoint] - "main.py" | "main.js"
 * @returns {Promise<Blob>}
 */
export const packageCodeToZip = async ({
  code,
  files,
  environment,
  entryPoint,
}) => {
  const zip = new JSZip();
  const defaultEntry =
    entryPoint || (environment === "python" ? "main.py" : "main.js");

  if (Array.isArray(files) && files.length > 0) {
    files.forEach((file) => {
      if (file.type !== "folder" && file.content !== undefined) {
        const filePath = file.path || file.name;
        zip.file(filePath, file.content || "");
      }
    });
  } else {
    zip.file(defaultEntry, code || "");
  }

  return await zip.generateAsync({
    type: "blob",
    compression: "DEFLATE",
    compressionOptions: { level: 6 },
  });
};

/**
 * Extract files from a downloaded project .zip Blob.
 * @param {Blob} zipBlob
 * @param {Object} options
 * @param {string} [options.environment="python"]
 * @param {string} [options.entryPoint="main.py"]
 * @returns {Promise<{ files: Array, entryCode: string }>}
 */
export const extractCodeFromZip = async (
  zipBlob,
  { environment = "python", entryPoint } = {}
) => {
  const zip = await JSZip.loadAsync(zipBlob);
  const files = [];
  const targetEntry =
    entryPoint || (environment === "python" ? "main.py" : "main.js");
  let entryCode = "";

  const entries = Object.keys(zip.files);
  for (const filename of entries) {
    const fileObj = zip.files[filename];
    if (!fileObj.dir) {
      const content = await fileObj.async("string");
      const isEntry = filename === targetEntry || filename.endsWith(`/${targetEntry}`);
      if (isEntry) {
        entryCode = content;
      }
      files.push({
        id: filename,
        name: filename.split("/").pop(),
        path: filename,
        content,
        type: "file",
        isEntry,
      });
    }
  }

  // If no entry matched specifically, use the first file's content
  if (!entryCode && files.length > 0) {
    files[0].isEntry = true;
    entryCode = files[0].content;
  }

  return { files, entryCode };
};
